import { useState } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, WarningCircle, CheckCircle, Copy, Buildings, Trash, ArrowRight, X } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

// Read-only "journal integrity" audit. Surfaces journals that are NOT backed by a live
// register line so already-posted duplicates/ghosts can be reversed in Xero, and lingering
// future 'ghost' pending journals (from a superseded import) can be cleaned up.
export default function IntegrityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allTenants, setAllTenants] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [purging, setPurging] = useState(false);
  const [retireTarget, setRetireTarget] = useState(null);
  const [retireChecked, setRetireChecked] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const [keptNotice, setKeptNotice] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/journal-integrity?all_tenants=${allTenants}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not run the integrity check");
    } finally { setLoading(false); }
  };

  const undoPurge = async (batchId) => {
    try {
      const { data } = await api.post("/journal-integrity/purge-pending-ghosts/undo", { batch_id: batchId });
      toast.success(`Restored ${data.restored} pending journal${data.restored === 1 ? "" : "s"}.`);
      run();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not undo the purge");
    }
  };

  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post(`/journal-integrity/purge-pending-ghosts?all_tenants=${allTenants}`);
      setConfirmPurge(false);
      if (data.purged > 0) {
        toast.success(
          `Purged ${data.purged} pending journal${data.purged === 1 ? "" : "s"} that couldn't post.`,
          { duration: 15000, action: { label: "Undo", onClick: () => undoPurge(data.batch_id) } });
      } else {
        toast.info("Nothing to purge — no pending ghosts found.");
      }
      run();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not purge pending ghosts");
    } finally { setPurging(false); }
  };

  const copyId = (id) => {
    if (!id) return;
    navigator.clipboard?.writeText(id);
    toast.success("Xero Journal ID copied");
  };

  const undoRetire = async (schedInfo) => {
    try {
      await api.post(`/schedules/${schedInfo.schedule_id}/restore`);
      toast.success(`Restored ${schedInfo.schedule_ref || "the line"}.`);
      run();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not undo — you may need to recreate the line");
    }
  };

  const doRetire = async () => {
    if (!retireTarget) return;
    setRetiring(true);
    try {
      const { data } = await api.post(`/schedules/${retireTarget.schedule_id}/retire`, { reversed_in_xero: true });
      const kept = retireTarget.kept;
      setRetireTarget(null); setRetireChecked(false);
      toast.success(
        `Retired ${retireTarget.schedule_ref || "the superseded line"} — ${data.journals_removed} journal${data.journals_removed === 1 ? "" : "s"} removed from the register.`,
        { duration: 15000, action: { label: "Undo", onClick: () => undoRetire(retireTarget) } });
      await run();
      if (kept) {
        setKeptNotice({
          ref: kept.schedule_ref || kept.schedule_contact || "the kept line",
          contact: kept.schedule_contact,
          q: kept.schedule_ref || kept.schedule_contact || "",
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not retire this schedule");
    } finally { setRetiring(false); }
  };

  const s = data?.summary;
  const clean = s && s.posted_ghosts === 0 && s.pending_ghosts === 0 && s.duplicate_groups === 0;

  return (
    <div className="mt-10 bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden" data-testid="integrity-panel">
      <div className="flex flex-wrap items-center gap-3 justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/60">
        <div className="flex items-center gap-2.5">
          <ShieldCheck size={20} weight="duotone" className="text-orange" />
          <div>
            <div className="font-heading font-bold text-navy text-base leading-tight">Journal integrity check</div>
            <p className="text-xs text-navy/60 mt-0.5">Finds posted duplicates, ghost journals in Xero, and pending journals not linked to a live register line.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-navy/70 cursor-pointer select-none" data-testid="integrity-scope-toggle">
            <input type="checkbox" checked={allTenants} onChange={(e) => setAllTenants(e.target.checked)}
              className="accent-orange" />
            All organisations
          </label>
          <button onClick={run} disabled={loading} data-testid="integrity-run-btn"
            className="flex items-center gap-2 bg-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange transition-colors disabled:opacity-60">
            <ShieldCheck size={16} /> {loading ? "Checking…" : data ? "Re-run check" : "Run check"}
          </button>
        </div>
      </div>

      {!data && !loading && (
        <div className="px-5 py-8 text-sm text-navy/60" data-testid="integrity-idle">
          Run the check to audit this {allTenants ? "practice" : "organisation"}. Nothing is modified — it only reports.
        </div>
      )}
      {loading && <div className="py-12 text-center font-mono text-navy/65 text-sm tracking-widest">CHECKING…</div>}

      {data && !loading && (
        <div className="p-5 space-y-6" data-testid="integrity-results">
          {keptNotice && (
            <div className="flex items-center gap-3 bg-positive/10 border border-positive/30 rounded-lg px-4 py-3" data-testid="integrity-kept-notice">
              <CheckCircle size={20} weight="fill" className="text-positive shrink-0" />
              <span className="text-sm text-navy flex-1">
                Retired the superseded line. <b>{keptNotice.ref}</b> should now be the only line for this entry.
              </span>
              <Link to={`/register?q=${encodeURIComponent(keptNotice.q)}`} data-testid="integrity-kept-link"
                className="inline-flex items-center gap-1 text-sm font-semibold text-positive hover:gap-2 transition-all shrink-0">
                View on register <ArrowRight size={14} weight="bold" />
              </Link>
              <button onClick={() => setKeptNotice(null)} data-testid="integrity-kept-dismiss" className="text-navy/40 hover:text-navy shrink-0">
                <X size={16} />
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="integrity-summary">
            <SummaryStat label="Duplicate journals" value={s.duplicate_journals} tone={s.duplicate_journals ? "negative" : "positive"} />
            <SummaryStat label="Ghost posts in Xero" value={s.posted_ghosts} tone={s.posted_ghosts ? "negative" : "positive"} />
            <SummaryStat label="Pending ghosts (won't post)" value={s.pending_ghosts} tone={s.pending_ghosts ? "warn" : "positive"} />
          </div>

          {clean && (
            <div className="flex items-center gap-3 bg-positive/10 border border-positive/30 text-positive rounded-lg px-5 py-3.5" data-testid="integrity-all-clear">
              <CheckCircle size={22} weight="fill" />
              <span className="text-sm font-medium">Every posted journal ties back to a live register line, and there are no ghost or duplicate entries.</span>
            </div>
          )}

          {s.duplicate_groups > 0 && (
            <Section title="Duplicates posted to Xero" testid="integrity-duplicates" tone="negative"
              help="The same entry was posted from two different schedules. Reverse the EXTRA journal in Xero (keep one), then retire the superseded schedule below.">
              {data.duplicates.map((g, i) => (
                <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 bg-slate-50 text-xs text-navy/70">
                    <span className="flex items-center gap-1"><Buildings size={12} /> {g.tenant_name}</span>
                    <span><b>{g.date}</b></span>
                    <span className="font-mono">{money(g.amount)}</span>
                    <span className="font-mono">codes: {g.account_codes.join(", ") || "—"}</span>
                    <span className="ml-auto text-negative font-semibold">{g.count} copies</span>
                  </div>
                  <MiniTable rows={g.journals} onCopy={copyId} retirable
                    onRetire={(r, siblings) => { setRetireTarget({ ...r, kept: siblings[0] }); setRetireChecked(false); }} />
                </div>
              ))}
            </Section>
          )}

          {s.posted_ghosts > 0 && (
            <Section title="Ghost journals in Xero (no schedule)" testid="integrity-posted-ghosts" tone="negative"
              help="These were posted to Xero but their schedule was deleted/superseded, so they no longer appear on the register. Review each in Xero and reverse if it shouldn't be there.">
              <MiniTable rows={data.posted_ghosts} onCopy={copyId} showReason />
            </Section>
          )}

          {s.pending_ghosts > 0 && (
            <Section title="Pending journals not on a live register line" testid="integrity-pending-ghosts" tone="warn"
              help="These will NOT post (the month-end task only posts live register lines), but they should be cleaned up. Purge them below (with an undo window), or delete the underlying superseded schedule."
              action={
                <button onClick={() => setConfirmPurge(true)} data-testid="integrity-purge-btn"
                  className="flex items-center gap-1.5 text-xs font-semibold bg-warn/15 text-navy border border-warn/40 px-3 py-1.5 rounded-lg hover:bg-warn/25 transition-colors shrink-0">
                  <Trash size={14} /> Purge {s.pending_ghosts}
                </button>
              }>
              <MiniTable rows={data.pending_ghosts} onCopy={copyId} showReason pending />
            </Section>
          )}
        </div>
      )}

      <AlertDialog open={confirmPurge} onOpenChange={setConfirmPurge}>
        <AlertDialogContent data-testid="integrity-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Purge {data?.summary?.pending_ghosts} pending ghost journal{data?.summary?.pending_ghosts === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              These pending journals aren't linked to a live register line, so they can never post. Purging removes them from {allTenants ? "all organisations" : "this organisation"}. This only touches un-postable pending journals — nothing posted to Xero, and no live schedules are affected. You'll have a short window to undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="integrity-purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={purge} disabled={purging} data-testid="integrity-purge-confirm"
              className="bg-warn text-navy hover:bg-warn/90">
              {purging ? "Purging…" : "Purge journals"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!retireTarget} onOpenChange={(o) => { if (!o) { setRetireTarget(null); setRetireChecked(false); } }}>
        <AlertDialogContent data-testid="integrity-retire-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Retire superseded schedule {retireTarget?.schedule_ref ? `(${retireTarget.schedule_ref})` : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the superseded schedule{retireTarget?.schedule_contact ? ` for ${retireTarget.schedule_contact}` : ""} and its {retireTarget?.amount != null ? money(retireTarget.amount) : ""} journal(s) from the register, so the duplicate stops recurring. Only do this <b>after</b> you've reversed its journal in Xero — otherwise Xero and the register will disagree. You'll have a short window to undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-start gap-2 text-sm text-navy bg-warn/10 border border-warn/30 rounded-lg px-3 py-2.5 my-1 cursor-pointer" data-testid="integrity-retire-confirm-check">
            <input type="checkbox" checked={retireChecked} onChange={(e) => setRetireChecked(e.target.checked)} className="accent-orange mt-0.5" />
            <span>I've already reversed this schedule's journal in Xero.</span>
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="integrity-retire-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRetire} disabled={!retireChecked || retiring} data-testid="integrity-retire-confirm"
              className="bg-negative text-white hover:bg-negative/90 disabled:opacity-50">
              {retiring ? "Retiring…" : "Retire schedule"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SummaryStat({ label, value, tone }) {
  const color = tone === "negative" ? "text-negative" : tone === "warn" ? "text-warn" : "text-positive";
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
      <div className="overline text-navy/60">{label}</div>
      <div className={`font-heading font-black text-3xl tracking-tighter mt-1 tabular ${color}`}>{value}</div>
    </div>
  );
}

function Section({ title, help, children, testid, tone, action }) {
  const bar = tone === "negative" ? "border-negative/40" : "border-warn/40";
  return (
    <div data-testid={testid} className={`border-l-4 ${bar} pl-4`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2">
            <WarningCircle size={16} weight="fill" className={tone === "negative" ? "text-negative" : "text-warn"} /> {title}
          </h3>
          <p className="text-xs text-navy/60 mt-1 mb-3">{help}</p>
        </div>
        {action}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function MiniTable({ rows, onCopy, showReason, pending, retirable, onRetire }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-[640px]">
        <thead>
          <tr className="text-left text-navy/55 border-b border-slate-100">
            <th className="px-4 py-2 font-semibold">Date</th>
            <th className="px-4 py-2 font-semibold text-right">Amount</th>
            <th className="px-4 py-2 font-semibold">Description</th>
            {!pending && <th className="px-4 py-2 font-semibold">Xero Journal ID</th>}
            {pending && <th className="px-4 py-2 font-semibold">Schedule</th>}
            {showReason && <th className="px-4 py-2 font-semibold">Note</th>}
            {retirable && <th className="px-4 py-2 font-semibold text-right">Superseded schedule</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.journal_id} className="border-b border-slate-50" data-testid={`integrity-jrow-${r.journal_id}`}>
              <td className="px-4 py-2 font-mono">{r.date}</td>
              <td className="px-4 py-2 font-mono text-right">{money(r.amount)}</td>
              <td className="px-4 py-2 text-navy/75">{r.description || "—"}</td>
              {!pending && (
                <td className="px-4 py-2">
                  {r.xero_journal_id ? (
                    <button onClick={() => onCopy(r.xero_journal_id)} className="inline-flex items-center gap-1 font-mono text-navy/80 hover:text-orange transition-colors" title="Copy Xero Journal ID">
                      <Copy size={12} /> {r.xero_journal_id}
                    </button>
                  ) : "—"}
                </td>
              )}
              {pending && <td className="px-4 py-2 font-mono text-navy/60">{r.schedule_status || "—"}</td>}
              {showReason && <td className="px-4 py-2 text-navy/55">{r.reason}</td>}
              {retirable && (
                <td className="px-4 py-2 text-right">
                  {r.schedule_exists ? (
                    <button onClick={() => onRetire(r, rows.filter((x) => x.journal_id !== r.journal_id && x.schedule_exists))} data-testid={`integrity-retire-btn-${r.journal_id}`}
                      className="inline-flex items-center gap-1 text-[0.7rem] font-semibold bg-negative/10 text-negative border border-negative/30 px-2.5 py-1 rounded-md hover:bg-negative/20 transition-colors">
                      <Trash size={12} /> Retire{r.schedule_ref ? ` ${r.schedule_ref}` : " schedule"}
                    </button>
                  ) : (
                    <span className="text-navy/40 italic">already gone</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
